import { min } from 'lib/arrayUtils'
import { useMemo } from 'react'
import useOffersBestPrices from './useOfferBestPrices'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { useAppSelector } from './reduxHooks'
import { isLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import { isBookingWithinDateRange } from 'lib/dateUtils'
import { isLPCTacticalEnabled } from 'lib/offerUtils'

export default function useSearchTileInclusionUpsellBestPrice(offer: App.Offer | App.OfferSummary, filters?: App.OfferListFilters) {
  const isLoggedIn = useAppSelector(isLoggedInSelector)

  const minimumDuration = useMemo(() => {
    const packagesWithBonusInclusionsAndPrice = offer.packages.filter(p => (!!p.bonusInclusions && p.bonusInclusions.length > 0) && p.price > 0)
    const minimumPackage = min(packagesWithBonusInclusionsAndPrice, (p) => p.duration)
    return minimumPackage?.duration ?? 0
  }, [offer])

  const selectedDuration = useMemo(() => {
    return filters?.checkIn && filters?.checkOut ? moment(filters.checkOut).diff(moment(filters.checkIn), 'days') : undefined
  }, [filters])

  const filterParams = useMemo(() => ({
    checkIn: minimumDuration > 0 ? filters?.checkIn : undefined,
    checkOut: minimumDuration > 0 && filters?.checkIn ? moment(filters.checkIn).add(minimumDuration, 'days').format(ISO_DATE_FORMAT) : undefined,
    rooms: filters?.rooms,
  }), [minimumDuration, filters?.checkIn, filters?.rooms])

  const bestPrice = useOffersBestPrices(offer, filterParams)

  const bestPricePackage = bestPrice?.bestPricePackage
  const isAuthorised = isLoggedIn || !offer.walledGarden
  let meetTactical = false
  let shouldShowUpsell = minimumDuration > 0 && (bestPricePackage && selectedDuration ? selectedDuration < bestPricePackage.duration : false) && isAuthorised

  if (isLPCTacticalEnabled() && offer.type === OFFER_TYPE_ALWAYS_ON && bestPricePackage && bestPricePackage.hasTactical && selectedDuration) {
    const tacticalSchedules = bestPricePackage.roomRate?.schedules.filter(schedule => schedule.type == 'tactical')
    if (tacticalSchedules && tacticalSchedules.length > 0) {
      meetTactical = true
      const travelPeriod = tacticalSchedules[0].travelPeriod
      const withinDateRange = isBookingWithinDateRange(filters?.checkIn, filters?.checkOut, travelPeriod.from, travelPeriod.to)
      shouldShowUpsell = minimumDuration > 0 && (bestPricePackage && selectedDuration ? selectedDuration <= bestPricePackage.duration : false) && !withinDateRange
    }
  }

  return {
    bestPrice: minimumDuration > 0 ? bestPrice : undefined,
    shouldShowUpsell,
    meetTactical,
  }
}
